import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';
import {
  BarChart3,
  BookMarked,
  BookOpen,
  Cake,
  Clock,
  Compass,
  FileQuestion,
  Globe,
  LogOut,
  Megaphone,
  Radio,
  School,
  Settings,
  Swords,
  Trophy,
  Users,
  X,
  Menu
} from 'lucide-react';

import Avatar from '../components/Avatar';
import InstallPwaBanner from '../components/InstallPwaBanner';
import LevelProgressionCard from '../components/LevelProgressionCard';
import { useLanguage } from '../context/LanguageContext';
import { API_URL } from '../lib/api';

function Dashboard({ onSessionExpired }) {
  const [user, setUser] = useState(null);
  const [progression, setProgression] = useState(null);
  const [answeredQuestions, setAnsweredQuestions] = useState([]);
  const [welcomeMsg, setWelcomeMsg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showBirthday, setShowBirthday] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { language, t } = useLanguage();

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (!user?.date_of_birth) return;
    const today = new Date();
    const todayKey = `${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    const dobKey = user.date_of_birth.slice(5);
    if (todayKey !== dobKey) return;

    const seenKey = `birthday_seen_${user.id}_${today.getFullYear()}`;
    const alreadyCelebrated = localStorage.getItem(seenKey);
    setShowBirthday(true);
    if (!alreadyCelebrated) {
      const fire = (delay, opts) => setTimeout(() => {
        confetti({ particleCount: 80, spread: 70, origin: { y: 0.4 }, ...opts });
      }, delay);
      fire(300, { angle: 60, origin: { x: 0, y: 0.5 } });
      fire(600, { angle: 120, origin: { x: 1, y: 0.5 } });
      fire(900, { particleCount: 120, spread: 100, origin: { y: 0.3 } });
      localStorage.setItem(seenKey, '1');
    }
  }, [user]);

  const fetchData = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [userRes, welcomeRes, progressionRes, answeredRes] = await Promise.all([
        axios.get(`${API_URL}/api/auth/me`, { headers }),
        axios.get(`${API_URL}/api/welcome-message`, { headers }),
        axios.get(`${API_URL}/api/user/progression`, { headers }),
        axios.get(`${API_URL}/api/progress/answered-questions?limit=1000`, { headers })
      ]);

      setUser(userRes.data);
      setWelcomeMsg(welcomeRes.data);
      setProgression(progressionRes.data);
      setAnsweredQuestions(answeredRes.data || []);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        onSessionExpired?.();
        navigate('/login', { replace: true });
      } else {
        toast.error(language === 'zh' ? '\u65e0\u6cd5\u52a0\u8f7d\u6570\u636e' : 'Failed to load data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    onSessionExpired?.();
    toast.success(language === 'zh' ? '\u9000\u51fa\u6210\u529f' : 'Logged out successfully');
    setTimeout(() => navigate('/login', { replace: true }), 500);
  };

  const navGroups = [
    {
      title: language === 'zh' ? '\u5b66\u4e60' : 'Learning',
      items: [
        { label: language === 'zh' ? '\u5173\u5361\u602a\u517d' : 'Level Monster', icon: BookOpen, path: '/levels?monster=level', testId: 'side-level-monster' },
        { label: language === 'zh' ? '\u7ade\u6280\u602a\u517d' : 'Battle Monster', icon: Swords, path: '/levels?monster=battle', testId: 'side-battle-monster' },
        { label: language === 'zh' ? '\u63a2\u7d22\u602a\u517d' : 'Exploration Monster', icon: Compass, path: '/levels?monster=exploration', testId: 'side-exploration-monster' }
      ]
    },
    {
      title: language === 'zh' ? '\u7ba1\u7406' : 'Management',
      adminOnly: true,
      items: [
        { label: language === 'zh' ? '\u9898\u76ee' : 'Questions', icon: FileQuestion, path: '/admin/questions', testId: 'side-admin-questions' },
        { label: language === 'zh' ? '\u7528\u6237' : 'Users', icon: Users, path: '/admin/users', testId: 'side-admin-users' },
        { label: language === 'zh' ? '\u5b66\u6821' : 'Schools', icon: School, path: '/admin/schools', testId: 'side-admin-schools' },
        { label: language === 'zh' ? '\u79d1\u76ee' : 'Subjects', icon: BookMarked, path: '/admin/subjects', testId: 'side-admin-subjects' },
        { label: language === 'zh' ? '\u5207\u6362\u8bed\u8a00' : 'Switch Language', icon: Globe, path: '/admin/translations', testId: 'side-admin-translations' },
        { label: language === 'zh' ? '\u516c\u544a' : 'Notices', icon: Megaphone, path: '/admin/notices', testId: 'side-admin-notices' }
      ]
    },
    {
      title: language === 'zh' ? '\u6210\u7ee9\u4e0e\u62a5\u544a' : 'Results',
      items: [
        { label: language === 'zh' ? '\u6211\u7684\u6392\u884c\u699c' : 'My Board', icon: Trophy, path: '/leaderboard', testId: 'side-leaderboard' },
        { label: language === 'zh' ? '\u62a5\u544a' : 'Reports', icon: BarChart3, path: '/admin/reports', adminOnly: true, testId: 'side-admin-reports' }
      ]
    },
    {
      title: language === 'zh' ? '\u7ade\u8d5b' : 'Competition',
      items: [
        { label: language === 'zh' ? '\u5b9e\u65f6\u7ade\u8d5b' : 'LIVE Competition', icon: Radio, path: '/live', testId: 'side-live' }
      ]
    },
    {
      title: language === 'zh' ? '\u8d26\u6237' : 'Account',
      items: [
        { label: language === 'zh' ? '\u4e2a\u4eba\u8d44\u6599' : 'Profile', icon: Settings, path: '/settings', testId: 'side-settings' },
        { label: language === 'zh' ? '\u9000\u51fa\u767b\u5f55' : 'Logout', icon: LogOut, action: handleLogout, danger: true, testId: 'side-logout' }
      ]
    }
  ];

  const renderMenuGroups = (testPrefix = 'side') => (
    navGroups
      .filter((group) => !group.adminOnly || user?.role === 'admin')
      .map((group) => {
        const items = group.items.filter((item) => !item.adminOnly || user?.role === 'admin');
        if (items.length === 0) return null;

        return (
          <div key={group.title}>
            <p className="mb-2 px-2 text-xs font-black uppercase tracking-wide text-zinc-400">
              {group.title}
            </p>
            <div className="space-y-1">
              {items.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.path || item.testId}
                    onClick={() => {
                      setMobileMenuOpen(false);
                      if (item.action) {
                        item.action();
                      } else {
                        navigate(item.path);
                      }
                    }}
                    className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm font-bold ${
                      item.danger
                        ? 'text-red-600 hover:bg-red-50'
                        : 'text-zinc-700 hover:bg-violet-50 hover:text-violet-700'
                    }`}
                    data-testid={testPrefix === 'mobile' ? item.testId.replace('side-', 'mobile-') : item.testId}
                  >
                    <Icon className="h-5 w-5 shrink-0" />
                    <span className="min-w-0 truncate">{item.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })
  );

  const groupAnsweredQuestions = () => {
    const subjectGroups = [];
    const subjectIndex = new Map();

    answeredQuestions.filter((item) => item.is_correct).forEach((item) => {
      const subjectKey = item.subject_id || 'unknown';
      if (!subjectIndex.has(subjectKey)) {
        const subjectGroup = {
          key: subjectKey,
          label: language === 'zh'
            ? (item.subject_name_zh || item.subject_name_en || subjectKey)
            : (item.subject_name_en || item.subject_name_zh || subjectKey),
          forms: [],
          formIndex: new Map()
        };
        subjectIndex.set(subjectKey, subjectGroup);
        subjectGroups.push(subjectGroup);
      }

      const subjectGroup = subjectIndex.get(subjectKey);
      const formKey = item.form_name || (language === 'zh' ? '\u672a\u5206\u7ea7' : 'No Form');
      if (!subjectGroup.formIndex.has(formKey)) {
        const formGroup = { key: formKey, label: formKey, chapters: [], chapterIndex: new Map() };
        subjectGroup.formIndex.set(formKey, formGroup);
        subjectGroup.forms.push(formGroup);
      }

      const formGroup = subjectGroup.formIndex.get(formKey);
      const chapterKey = item.chapter || (language === 'zh' ? '\u672a\u5206\u7ae0' : 'No Chapter');
      if (!formGroup.chapterIndex.has(chapterKey)) {
        const chapterGroup = { key: chapterKey, label: chapterKey, rows: [] };
        formGroup.chapterIndex.set(chapterKey, chapterGroup);
        formGroup.chapters.push(chapterGroup);
      }

      formGroup.chapterIndex.get(chapterKey).rows.push(item);
    });

    return subjectGroups;
  };

  const answeredGroups = groupAnsweredQuestions();
  const correctAnswerCount = answeredQuestions.filter((item) => item.is_correct).length;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-violet-50 to-pink-50">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-violet-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-white to-pink-50" data-testid="dashboard">
      <main className="max-w-7xl mx-auto px-4 py-6">
        <div className="relative mb-4 lg:hidden" data-testid="dashboard-mobile-menu">
          <button
            type="button"
            onClick={() => setMobileMenuOpen((open) => !open)}
            className="flex w-full items-center justify-between rounded-2xl border-2 border-zinc-200 bg-white px-4 py-3 font-black text-zinc-900 shadow-sm"
            aria-expanded={mobileMenuOpen}
            aria-controls="dashboard-mobile-menu-panel"
            data-testid="dashboard-mobile-menu-btn"
          >
            <span className="flex items-center gap-2">
              <Menu className="h-5 w-5 text-violet-600" />
              {language === 'zh' ? '\u83dc\u5355' : 'Menu'}
            </span>
            <span className="max-w-[45vw] truncate text-xs font-bold text-zinc-400">{user?.username}</span>
          </button>

          {mobileMenuOpen && (
            <div
              id="dashboard-mobile-menu-panel"
              className="absolute left-0 right-0 top-full z-40 mt-2 max-h-[70vh] overflow-y-auto rounded-2xl border-2 border-zinc-200 bg-white p-4 shadow-xl"
              data-testid="dashboard-mobile-menu-panel"
            >
              <div className="mb-4 flex items-center gap-3 border-b border-zinc-100 pb-4">
                <Avatar
                  src={user?.profile_picture}
                  name={user?.full_name || user?.username}
                  size={44}
                  className="ring-2 ring-violet-200"
                />
                <div className="min-w-0">
                  <p className="truncate text-sm font-black text-zinc-900">{user?.full_name || user?.username}</p>
                  <p className="truncate text-xs font-bold text-zinc-400">@{user?.username}</p>
                </div>
              </div>
              <nav className="space-y-4">
                {renderMenuGroups('mobile')}
              </nav>
            </div>
          )}
        </div>

        <div className="grid gap-6 lg:grid-cols-[280px_1fr]">
          <aside className="hidden lg:block lg:sticky lg:top-24 lg:self-start" data-testid="dashboard-side-menu">
            <div className="rounded-2xl border-2 border-zinc-200 bg-white p-4 shadow-sm">
              <div className="mb-4 flex items-center gap-3 border-b border-zinc-100 pb-4">
                <Avatar
                  src={user?.profile_picture}
                  name={user?.full_name || user?.username}
                  size={44}
                  className="ring-2 ring-violet-200"
                />
                <div className="min-w-0">
                  <p className="truncate text-sm font-black text-zinc-900">{user?.full_name || user?.username}</p>
                  <p className="truncate text-xs font-bold text-zinc-400">@{user?.username}</p>
                </div>
              </div>

              <nav className="space-y-4">
                {renderMenuGroups()}
              </nav>
            </div>
          </aside>

          <section className="min-w-0">
            <InstallPwaBanner language={language} role={user?.role || 'user'} />

            <div className="mb-8 flex flex-col gap-6 md:flex-row md:items-center">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="flex justify-center md:w-56 lg:w-64 shrink-0"
                data-testid="dashboard-mascot"
              >
                <Avatar
                  src={user?.profile_picture}
                  name={user?.full_name || user?.username}
                  size={190}
                  className="ring-4 ring-white shadow-xl"
                />
              </motion.div>

              <div className="flex-1 min-w-0">
                <motion.div
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <h1 className="text-3xl md:text-4xl font-black text-zinc-900 mb-2" data-testid="welcome-title">
                    {t('welcome_back')}, {user?.full_name || user?.username}!
                  </h1>
                  {welcomeMsg && (
                    <p className="text-zinc-600 font-medium">
                      {language === 'zh' ? welcomeMsg.message_zh : welcomeMsg.message_en}
                    </p>
                  )}
                  {user?.previous_login_at && (
                    <div className="mt-3 flex items-center gap-1.5 text-sm font-medium text-zinc-500" data-testid="last-login-display">
                      <Clock className="w-4 h-4 text-zinc-400" />
                      <span>
                        {language === 'zh' ? '\u4e0a\u6b21\u767b\u5f55:' : 'Last login:'}{' '}
                        <span className="text-zinc-700">
                          {new Date(user.previous_login_at).toLocaleString(
                            language === 'zh' ? 'zh-CN' : 'en-US',
                            { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }
                          )}
                        </span>
                      </span>
                    </div>
                  )}
                </motion.div>
              </div>
            </div>

            {showBirthday && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9, y: -10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ duration: 0.5, type: 'spring' }}
                className="relative mb-6 overflow-hidden rounded-2xl border-2 border-pink-300 bg-gradient-to-r from-pink-100 via-yellow-100 to-violet-100 p-5 shadow-md"
                data-testid="birthday-banner"
              >
                <button
                  onClick={() => setShowBirthday(false)}
                  className="absolute top-2 right-2 p-1 rounded-full hover:bg-white/60 transition-colors"
                  data-testid="close-birthday-btn"
                  aria-label="dismiss"
                >
                  <X className="w-4 h-4 text-zinc-600" />
                </button>
                <div className="flex items-center gap-4">
                  <div className="bg-white rounded-2xl p-3 shadow-sm shrink-0">
                    <Cake className="w-12 h-12 text-pink-500" data-testid="birthday-cake-icon" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-xl md:text-2xl font-black text-pink-600">
                      {language === 'zh' ? '\u751f\u65e5\u5feb\u4e50!' : 'Happy Birthday!'}
                    </h3>
                    <p className="text-sm md:text-base font-medium text-zinc-700 mt-0.5">
                      {language === 'zh'
                        ? `${user?.full_name || user?.username}, \u795d\u4f60\u4eca\u5929\u8fc7\u5f97\u5f00\u5fc3, \u7ee7\u7eed\u52a0\u6cb9\u5b66\u4e60!`
                        : `Have an amazing day, ${user?.full_name || user?.username}! Keep crushing your quizzes!`}
                    </p>
                  </div>
                </div>
              </motion.div>
            )}

            {progression && (
              <div className="mb-6">
                <LevelProgressionCard progression={progression} language={language} />
              </div>
            )}

            <section className="rounded-2xl border-2 border-zinc-200 bg-white shadow-sm" data-testid="answered-questions-table">
              <div className="border-b border-zinc-100 p-5">
                <div className="flex flex-wrap items-end justify-between gap-3">
                  <div>
                    <p className="text-xs font-black uppercase tracking-wide text-zinc-400">
                      {language === 'zh' ? '\u6b63\u786e\u7b54\u9898' : 'Correct Answers'}
                    </p>
                    <h2 className="mt-1 text-2xl font-black text-zinc-900">
                      {language === 'zh' ? '\u6309\u79d1\u76ee / \u5e74\u7ea7 / \u7ae0\u8282' : 'Subject / Form / Chapter'}
                    </h2>
                  </div>
                  <span className="rounded-xl bg-violet-50 px-3 py-1.5 text-sm font-black text-violet-700">
                    {correctAnswerCount} {language === 'zh' ? '\u9898' : 'questions'}
                  </span>
                </div>
              </div>

              {answeredGroups.length === 0 ? (
                <div className="p-8 text-center">
                  <p className="font-bold text-zinc-500">
                    {language === 'zh' ? '\u6682\u65e0\u6b63\u786e\u7b54\u9898' : 'No correct answers yet'}
                  </p>
                </div>
              ) : (
                <div className="space-y-4 p-4">
                  {answeredGroups.map((subjectGroup) => (
                    <div key={subjectGroup.key} className="overflow-hidden rounded-lg border border-zinc-200">
                      <div className="bg-zinc-950 px-4 py-3 text-sm font-black text-white">
                        {subjectGroup.label}
                      </div>

                      {subjectGroup.forms.map((formGroup) => (
                        <div key={`${subjectGroup.key}-${formGroup.key}`} className="border-t border-zinc-200">
                          <div className="bg-white px-4 py-3 text-xs font-black uppercase tracking-wide text-zinc-500">
                            {language === 'zh' ? '\u5e74\u7ea7' : 'FORM'}: <span className="text-teal-700">{formGroup.label}</span>
                          </div>

                          {formGroup.chapters.map((chapterGroup) => (
                            <div
                              key={`${subjectGroup.key}-${formGroup.key}-${chapterGroup.key}`}
                              className="flex items-center justify-between gap-3 border-t border-zinc-100 bg-violet-50 px-4 py-3"
                            >
                              <p className="min-w-0 truncate text-sm font-black text-violet-700">
                                {language === 'zh' ? '\u7ae0\u8282' : 'Chapter'}: {chapterGroup.label}
                              </p>
                              <p className="shrink-0 text-xs font-bold text-violet-700">
                                {chapterGroup.rows.length} {language === 'zh' ? '\u9898' : 'questions'}
                              </p>
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </section>
          </section>
        </div>
      </main>
    </div>
  );
}

export default Dashboard;
